import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../../i18n/LanguageContext';
import { HomeIcon } from '../../utils/IconWrapper';
import Section from '../../components/ui/Section';
import Button from '../../components/ui/Button';
import { Helmet } from 'react-helmet';

const PageContainer = styled.div`
  padding-top: 70px;
`;

const HeroSection = styled.div`
  background-image: url('https://images.unsplash.com/photo-1484154218962-a197022b5858?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80');
  background-size: cover;
  background-position: center;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const HeroContent = styled.div`
  position: relative;
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: white;
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const ServiceTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: var(--accent-primary);
  }
`;

const ServiceDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: 1.5rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  
  svg {
    margin-top: 5px;
    margin-right: 1rem;
    min-width: 24px;
    color: var(--accent-primary);
  }
`;

const FeatureText = styled.div`
  h3 {
    margin-bottom: 0.5rem;
    font-size: 1.2rem;
  }
  
  p {
    color: var(--text-secondary);
    line-height: 1.6;
  }
`;

const CallToAction = styled.div`
  background-color: var(--bg-secondary);
  padding: 3rem;
  text-align: center;
  border-radius: 8px;
  margin: 3rem 0;
`;

const CTATitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const CTAText = styled.p`
  color: var(--text-secondary);
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const LongTermRentals: React.FC = () => {
  const { language } = useLanguage();
  
  return (
    <PageContainer>
      <Helmet>
        <title>
          {language === 'it' 
            ? 'Affitti Medio/Lungo Termine | Servizi J-O-H' 
            : 'Medium/Long-Term Rentals | J-O-H Services'}
        </title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Servizi di affitto a lungo termine: appartamenti arredati per soggiorni prolungati in tutta Italia.'
            : 'Long-term rental services: furnished apartments for extended stays throughout Italy.'}
        />
        <meta 
          name="keywords" 
          content={[
            'Long-Term Rentals',
            'Furnished Apartments', 
            'Extended Stays',
            'Italy',
            'Accommodation Services',
            'J-O-H'
          ].join(', ')}
        />
      </Helmet>
      
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            {language === 'it' ? 'Affitti Medio/Lungo Termine' : 'Medium/Long-Term Rentals'}
          </HeroTitle>
          <HeroSubtitle>
            {language === 'it'
              ? 'Soluzioni abitative di qualità per chi cerca una sistemazione confortevole per periodi prolungati.'
              : 'Quality housing solutions for those seeking comfortable accommodation for extended periods.'}
          </HeroSubtitle>
          <Button variant="primary" to="/contact">
            {language === 'it' ? 'Richiedi Informazioni' : 'Request Information'}
          </Button>
        </HeroContent>
      </HeroSection>
      
      <Section padding="6rem 0">
        <Content>
          <ServiceTitle>
            {language === 'it' ? 'I Nostri Alloggi a Medio e Lungo Termine' : 'Our Medium and Long-Term Accommodations'}
          </ServiceTitle>
          
          <ServiceDescription>
            {language === 'it'
              ? 'Per chi cerca una soluzione abitativa più stabile, offriamo alloggi a medio e lungo termine completamente arredati e dotati di ogni comfort. Ideali per trasferimenti di lavoro, ristrutturazioni della propria abitazione o per chi desidera vivere un\'esperienza prolungata in una nuova località.'
              : 'For those seeking a more stable housing solution, we offer fully furnished medium and long-term accommodations equipped with every comfort. Ideal for work relocations, home renovations, or for those wishing to experience an extended stay in a new location.'}
          </ServiceDescription>
          
          <ServiceDescription>
            {language === 'it'
              ? 'I nostri alloggi a medio e lungo termine sono progettati per farti sentire a casa anche lontano da casa. Offriamo contratti flessibili che si adattano alle tue esigenze, con periodi che vanno da un mese a un anno o più.'
              : 'Our medium and long-term accommodations are designed to make you feel at home away from home. We offer flexible contracts that adapt to your needs, with periods ranging from one month to a year or more.'}
          </ServiceDescription>
          
          <FeatureList>
            <FeatureItem>
              <HomeIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Completamente Arredati' : 'Fully Furnished'}</h3>
                <p>
                  {language === 'it'
                    ? 'Tutti i nostri alloggi sono completamente arredati e dotati di elettrodomestici moderni, biancheria di qualità e tutto ciò di cui hai bisogno per un soggiorno confortevole.'
                    : 'All our accommodations are fully furnished and equipped with modern appliances, quality linens, and everything you need for a comfortable stay.'}
                </p>
              </FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <HomeIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Contratti Flessibili' : 'Flexible Contracts'}</h3>
                <p>
                  {language === 'it'
                    ? 'Offriamo opzioni di affitto flessibili, con contratti personalizzati in base alle tue esigenze di durata, con termini e condizioni trasparenti.'
                    : 'We offer flexible rental options, with customized contracts based on your duration needs, with transparent terms and conditions.'}
                </p>
              </FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <HomeIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Gestione Completa' : 'Full Management'}</h3>
                <p>
                  {language === 'it'
                    ? 'Ci occupiamo di tutti gli aspetti della gestione dell\'immobile, dalle utenze alla manutenzione, permettendoti di goderti il tuo soggiorno senza preoccupazioni.'
                    : 'We take care of all aspects of property management, from utilities to maintenance, allowing you to enjoy your stay without worries.'}
                </p>
              </FeatureText>
            </FeatureItem>
          </FeatureList>
          
          <CallToAction>
            <CTATitle>
              {language === 'it' ? 'Cerchi una Soluzione a Lungo Termine?' : 'Looking for a Long-Term Solution?'}
            </CTATitle>
            <CTAText>
              {language === 'it'
                ? 'Contattaci oggi per scoprire le nostre proprietà disponibili per affitti a medio e lungo termine e trovare la soluzione perfetta per le tue esigenze.'
                : 'Contact us today to discover our properties available for medium and long-term rentals and find the perfect solution for your needs.'}
            </CTAText>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci Ora' : 'Contact Us Now'}
            </Button>
          </CallToAction>
        </Content>
      </Section>
    </PageContainer>
  );
};

export default LongTermRentals; 